import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { firestoreService } from '../../shared/services/firestoreService';
import type { DishSnapshot } from '../../shared/types';
import { checkinMessage } from '../../shared/checkinErrors';
import { CheckIcon, XIcon, WarningIcon, ClockIcon, CameraIcon, RefreshIcon, WifiOffIcon } from './icons/UiIcons';

/**
 * Scan du QR wallet du client — moyen de secours quand le NFC ne marche pas (puce absente
 * ou abîmée, téléphone sans NFC, NFC désactivé). Le serveur applique la même logique que
 * le tap NFC (validateQrSession → checkin/perform.ts) : 1 validation / client / resto /
 * jour, prix de la carte, plafonds.
 *
 * Décodage : BarcodeDetector quand le navigateur l'a (Chrome Android/macOS, Safari 17.4+),
 * sinon jsQR (Firefox, Chrome Windows…), chargé à la demande. Saisie manuelle en dernier recours.
 */
interface ClientQrScannerProps {
    onClose: () => void;
    /** Scan depuis l'écran d'attente d'une addition : c'est elle qui est reliée au client. */
    pendingTransactionId?: string;
    /** When set, the scan will complete this reservation atomically (visit + membership + reservation). */
    linkedReservationId?: string;
    amountCents?: number;
    dishes?: DishSnapshot[];
    onSuccess?: (result: { pointsEarned: number; clientName: string }) => void;
}

type ScanStatus = 'idle' | 'starting' | 'scanning' | 'validating' | 'success' | 'already_today' | 'expired' | 'already_used' | 'offline' | 'error';
type CameraIssue = 'insecure_context' | 'unsupported_browser' | 'permission_denied' | 'no_camera' | 'camera_busy';

interface ScanResult {
    pointsEarned: number;
    clientName: string;
    /** « +2 tampons » / « +25 pts » selon le programme. */
    gainLabel?: string;
    /** Ce que le scan a relié : addition, réservation. */
    linkLabel?: string;
}

const CAMERA_MESSAGES: Record<CameraIssue, string> = {
    insecure_context: 'La caméra exige une connexion sécurisée (https). Ouvrez le tableau de bord en https, ou saisissez le code ci-dessous.',
    unsupported_browser: 'Ce navigateur ne donne pas accès à la caméra. Essayez Chrome ou Safari à jour, ou saisissez le code ci-dessous.',
    permission_denied: 'Accès à la caméra refusé. Autorisez la caméra pour ce site (icône à gauche de l\'adresse), puis réessayez.',
    no_camera: 'Aucune caméra détectée sur cet appareil. Saisissez le code affiché sous le QR du client.',
    camera_busy: 'La caméra est utilisée par une autre application ou n\'a pas pu démarrer. Fermez-la puis réessayez.',
};

const formatEuros = (cents: number) => `${(cents / 100).toFixed(2).replace('.', ',')} €`;

type Detect = (video: HTMLVideoElement) => Promise<string | null>;

/** Choisit le décodeur : BarcodeDetector natif si disponible pour les QR, sinon jsQR. */
async function createDetector(): Promise<Detect> {
    const BD = (window as any).BarcodeDetector;
    if (BD) {
        try {
            const formats: string[] = typeof BD.getSupportedFormats === 'function' ? await BD.getSupportedFormats() : ['qr_code'];
            if (formats.includes('qr_code')) {
                const detector = new BD({ formats: ['qr_code'] });
                return async (video) => {
                    const codes: Array<{ rawValue?: string }> = await detector.detect(video);
                    return codes[0]?.rawValue ?? null;
                };
            }
        } catch { /* repli jsQR */ }
    }
    const { default: jsQR } = await import('jsqr');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    return async (video) => {
        if (!ctx || !video.videoWidth) return null;
        const scale = Math.min(1, 640 / video.videoWidth);
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        return jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' })?.data ?? null;
    };
}

const ClientQrScanner: React.FC<ClientQrScannerProps> = ({
    onClose,
    pendingTransactionId,
    linkedReservationId,
    amountCents,
    dishes,
    onSuccess,
}) => {
    const titleId = useId();
    const [status, setStatus] = useState<ScanStatus>('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const [result, setResult] = useState<ScanResult | null>(null);
    const [manualInput, setManualInput] = useState('');
    const [cameraIssue, setCameraIssue] = useState<CameraIssue | null>(null);

    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const openRef = useRef(true);          // false dès la fermeture (même pendant la demande de permission)
    const detectBusyRef = useRef(false);   // une seule détection à la fois
    const handlingRef = useRef(false);     // un seul QR traité à la fois

    const stopCamera = useCallback(() => {
        if (scanIntervalRef.current) { clearInterval(scanIntervalRef.current); scanIntervalRef.current = null; }
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
        if (videoRef.current) videoRef.current.srcObject = null;
    }, []);

    const close = useCallback(() => {
        openRef.current = false;
        stopCamera();
        onClose();
    }, [onClose, stopCamera]);

    useEffect(() => {
        openRef.current = true;
        return () => { openRef.current = false; stopCamera(); };
    }, [stopCamera]);

    // Échap ferme la fenêtre.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [close]);

    const handleNonce = async (raw: string) => {
        if (handlingRef.current) return;
        handlingRef.current = true;
        stopCamera();
        const nonce = raw.trim();
        if (!nonce || nonce.length > 64 || nonce.includes('/')) {
            setErrorMsg('Ce QR n\'est pas un QR de fidélité ATABLE : demandez au client d\'ouvrir son QR dans l\'application.');
            setStatus('error');
            return;
        }
        setStatus('validating');

        // restaurantId extrait côté serveur depuis le JWT restaurateur — pas de client
        const res = await firestoreService.validateQrSession(nonce, {
            pendingTransactionId,
            linkedReservationId,
            amountCents,
            dishes,
        });
        if (!openRef.current) return;

        if (res.success) {
            const stamps = res.stampsEarned ?? 0;
            const isStamps = res.programType === 'stamps' || (res.pointsEarned === 0 && stamps > 0);
            const gainLabel = isStamps
                ? `+${stamps} tampon${stamps > 1 ? 's' : ''}`
                : `+${res.pointsEarned} pts`;
            const amount = typeof res.amountCents === 'number' && res.amountCents > 0 ? ` · ${formatEuros(res.amountCents)}` : '';
            const linkLabel = res.linked === 'pending'
                ? `Addition reliée${amount}`
                : res.linked === 'reservation' ? `Réservation terminée${amount}` : undefined;
            setResult({ pointsEarned: res.pointsEarned, clientName: res.clientName || 'Client', gainLabel, linkLabel });
            setStatus('success');
            onSuccess?.({ pointsEarned: res.pointsEarned, clientName: res.clientName || 'Client' });
            return;
        }
        switch (res.message) {
            case 'already_validated_today':
                setResult({ pointsEarned: 0, clientName: res.clientName || 'Client' });
                setStatus('already_today');
                return;
            case 'session_expired': setStatus('expired'); return;
            case 'session_already_used': setStatus('already_used'); return;
            case 'offline': setStatus('offline'); return;
            default:
                setErrorMsg(checkinMessage(res.message ?? ''));
                setStatus('error');
        }
    };

    const startCamera = async () => {
        setCameraIssue(null);
        if (!window.isSecureContext) { setCameraIssue('insecure_context'); return; }
        if (!navigator.mediaDevices?.getUserMedia) { setCameraIssue('unsupported_browser'); return; }
        setStatus('starting');

        let stream: MediaStream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: 'environment' }, width: { ideal: 640 }, height: { ideal: 480 } },
            });
        } catch (e: any) {
            const name = String(e?.name ?? '');
            setCameraIssue(
                name === 'NotAllowedError' || name === 'SecurityError' ? 'permission_denied'
                    : name === 'NotFoundError' || name === 'OverconstrainedError' ? 'no_camera'
                        : name === 'NotReadableError' || name === 'AbortError' ? 'camera_busy'
                            : 'unsupported_browser',
            );
            setStatus('idle');
            return;
        }
        // Fenêtre fermée pendant la demande de permission : on coupe la caméra aussitôt.
        if (!openRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        setStatus('scanning');

        // La vidéo n'existe qu'à l'état « scanning » : attendre le rendu.
        await new Promise((r) => requestAnimationFrame(() => r(null)));
        const video = videoRef.current;
        if (!video || !openRef.current) { stopCamera(); return; }
        video.srcObject = stream;
        try {
            await video.play();
        } catch {
            stopCamera();
            setCameraIssue('camera_busy');
            setStatus('idle');
            return;
        }

        let detect: Detect;
        try {
            detect = await createDetector();
        } catch {
            stopCamera();
            setCameraIssue('unsupported_browser');
            setStatus('idle');
            return;
        }
        if (!openRef.current) { stopCamera(); return; }
        scanIntervalRef.current = setInterval(async () => {
            const v = videoRef.current;
            if (!v || v.readyState < 2 || detectBusyRef.current || handlingRef.current) return;
            detectBusyRef.current = true;
            try {
                const value = await detect(v);
                if (value) await handleNonce(value);
            } catch { /* image pas prête */ } finally {
                detectBusyRef.current = false;
            }
        }, 250);
    };

    const handleManualSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!manualInput.trim()) return;
        await handleNonce(manualInput);
    };

    const reset = () => {
        handlingRef.current = false;
        setStatus('idle');
        setResult(null);
        setErrorMsg('');
        setManualInput('');
    };

    const liveMessage =
        status === 'starting' ? 'Ouverture de la caméra…'
            : status === 'scanning' ? 'Caméra active : pointez vers le QR du client.'
                : status === 'validating' ? 'Validation en cours…'
                    : status === 'success' && result ? `Visite validée pour ${result.clientName}. ${result.gainLabel ?? ''}`
                        : status === 'already_today' ? 'Déjà validé aujourd\'hui.'
                            : status === 'expired' ? 'QR expiré.'
                                : status === 'already_used' ? 'QR déjà utilisé.'
                                    : status === 'offline' ? 'Pas de connexion internet.'
                                        : status === 'error' ? errorMsg
                                            : cameraIssue ? CAMERA_MESSAGES[cameraIssue] : '';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
            >
                <p role="status" aria-live="polite" className="sr-only">{liveMessage}</p>

                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-slate-700">
                    <h2 id={titleId} className="text-lg font-black text-gray-900 dark:text-white">Scanner le QR du client</h2>
                    <button type="button" onClick={close} aria-label="Fermer" className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
                </div>

                {/* IDLE / CAMERA UNAVAILABLE */}
                {(status === 'idle' || status === 'starting') && (
                    <div className="p-6 space-y-4">
                        {cameraIssue ? (
                            <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded-xl text-center">{CAMERA_MESSAGES[cameraIssue]}</p>
                        ) : (
                            <p className="text-sm text-gray-500 text-center">Activez la caméra et scannez le QR affiché dans l'application du client.</p>
                        )}
                        {cameraIssue !== 'insecure_context' && cameraIssue !== 'no_camera' && (
                            <button
                                type="button"
                                onClick={startCamera}
                                disabled={status === 'starting'}
                                className="w-full py-4 bg-brand-primary text-white font-black rounded-lg text-base shadow-lg active:scale-95 transition-transform inline-flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                <CameraIcon className="w-5 h-5" />
                                {status === 'starting' ? 'Ouverture de la caméra…' : cameraIssue ? 'Réessayer la caméra' : 'Activer la caméra'}
                            </button>
                        )}
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                            <div className="relative text-center"><span className="bg-white dark:bg-slate-800 px-2 text-xs text-gray-400">ou saisie manuelle</span></div>
                        </div>

                        {/* Manual input */}
                        <form onSubmit={handleManualSubmit} className="space-y-3">
                            <label htmlFor={`${titleId}-manual`} className="sr-only">Code du QR du client</label>
                            <input
                                id={`${titleId}-manual`}
                                type="text"
                                value={manualInput}
                                onChange={e => setManualInput(e.target.value)}
                                placeholder="Code affiché sous le QR du client…"
                                autoComplete="off"
                                className="w-full border-2 border-gray-400 dark:border-slate-600 rounded-xl px-4 py-3 text-sm font-mono focus:border-brand-primary outline-none focus:ring-1 focus:ring-brand-500 dark:bg-slate-700 dark:text-white"
                            />
                            <button
                                type="submit"
                                disabled={!manualInput.trim()}
                                className="w-full py-3 bg-peach-100 text-brand-700 font-bold rounded-lg hover:bg-peach-200 disabled:opacity-40"
                            >
                                Valider
                            </button>
                        </form>
                    </div>
                )}

                {/* SCANNING */}
                {status === 'scanning' && (
                    <div className="p-4 space-y-3">
                        <p className="text-xs text-gray-400 text-center">Pointez vers le QR du client</p>
                        <div className="relative bg-black rounded-2xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
                            <video ref={videoRef} className="w-full h-full object-cover" muted playsInline aria-label="Image de la caméra" />
                            {/* Scan frame */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
                                <div className="w-48 h-48 border-4 border-white/70 rounded-2xl relative">
                                    <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-brand-secondary rounded-tl-lg" />
                                    <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-brand-secondary rounded-tr-lg" />
                                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-brand-secondary rounded-bl-lg" />
                                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-brand-secondary rounded-br-lg" />
                                </div>
                            </div>
                        </div>
                        <button type="button" onClick={() => { stopCamera(); reset(); }} className="w-full py-2 text-sm text-gray-500 hover:text-gray-700">Annuler</button>
                    </div>
                )}

                {/* VALIDATING */}
                {status === 'validating' && (
                    <div className="p-8 text-center space-y-3">
                        <div className="w-16 h-16 mx-auto rounded-full bg-brand-light dark:bg-brand-primary/20 text-brand-primary flex items-center justify-center animate-pulse" aria-hidden="true">
                            <ClockIcon className="w-8 h-8" />
                        </div>
                        <p className="font-bold text-gray-700 dark:text-gray-200">Validation en cours…</p>
                    </div>
                )}

                {/* SUCCESS */}
                {status === 'success' && result && (
                    <div className="p-8 text-center space-y-4">
                        <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center" aria-hidden="true">
                            <CheckIcon className="w-10 h-10" />
                        </div>
                        <div>
                            <h3 className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400">Visite validée !</h3>
                            <p className="text-gray-700 dark:text-gray-300 font-semibold mt-1">{result.clientName}</p>
                        </div>
                        <div className="bg-brand-secondary/10 rounded-2xl px-6 py-4">
                            <p className="text-3xl text-brand-secondary font-stat">{result.gainLabel ?? `+${result.pointsEarned} pts`}</p>
                            <p className="text-xs text-gray-500">ajoutés au compte fidélité</p>
                            {result.linkLabel && (
                                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mt-2">{result.linkLabel}</p>
                            )}
                        </div>
                        <button type="button" onClick={() => { reset(); close(); }} className="w-full py-3 bg-brand-primary text-white font-bold rounded-lg">Fermer</button>
                    </div>
                )}

                {/* ALREADY TODAY */}
                {status === 'already_today' && result && (
                    <div className="p-8 text-center space-y-4">
                        <div className="w-20 h-20 mx-auto rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center" aria-hidden="true">
                            <WarningIcon className="w-10 h-10" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-amber-600 dark:text-amber-400">Déjà validé aujourd'hui</h3>
                            <p className="text-gray-700 dark:text-gray-300 font-semibold mt-1">{result.clientName}</p>
                            <p className="text-sm text-gray-500 mt-1">Une validation par jour et par restaurant (tap, QR ou manuelle).</p>
                        </div>
                        <button type="button" onClick={() => { reset(); close(); }} className="w-full py-3 bg-gray-200 text-gray-800 font-bold rounded-lg">Fermer</button>
                    </div>
                )}

                {/* EXPIRED */}
                {status === 'expired' && (
                    <div className="p-8 text-center space-y-4">
                        <div className="w-20 h-20 mx-auto rounded-full bg-orange-100 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400 flex items-center justify-center" aria-hidden="true">
                            <ClockIcon className="w-10 h-10" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-orange-600">QR expiré</h3>
                            <p className="text-sm text-gray-500 mt-1">Demandez au client d'afficher un nouveau QR dans l'application.</p>
                        </div>
                        <button type="button" onClick={reset} className="w-full py-3 bg-brand-primary text-white font-bold rounded-lg">Réessayer</button>
                    </div>
                )}

                {/* ALREADY USED (double scan) */}
                {status === 'already_used' && (
                    <div className="p-8 text-center space-y-4">
                        <div className="w-20 h-20 mx-auto rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center" aria-hidden="true">
                            <RefreshIcon className="w-10 h-10" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-amber-600">QR déjà utilisé</h3>
                            <p className="text-sm text-gray-500 mt-1">Ce QR a déjà été scanné et validé. Vérifiez la fiche du client ; sinon, demandez-lui un nouveau QR.</p>
                        </div>
                        <button type="button" onClick={reset} className="w-full py-3 bg-brand-primary text-white font-bold rounded-lg">Scanner un autre client</button>
                    </div>
                )}

                {/* OFFLINE */}
                {status === 'offline' && (
                    <div className="p-8 text-center space-y-4">
                        <div className="w-20 h-20 mx-auto rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex items-center justify-center" aria-hidden="true">
                            <WifiOffIcon className="w-10 h-10" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-600">Pas de connexion internet</h3>
                            <p className="text-sm text-gray-500 mt-1">Vérifiez votre Wi-Fi ou vos données mobiles, puis réessayez.</p>
                        </div>
                        <button type="button" onClick={reset} className="w-full py-3 bg-brand-primary text-white font-bold rounded-lg">Réessayer</button>
                    </div>
                )}

                {/* ERROR */}
                {status === 'error' && (
                    <div className="p-8 text-center space-y-4">
                        <div className="w-20 h-20 mx-auto rounded-full bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400 flex items-center justify-center" aria-hidden="true">
                            <XIcon className="w-10 h-10" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-red-600 dark:text-red-400">Validation impossible</h3>
                            <p className="text-sm text-gray-500 mt-1">{errorMsg}</p>
                        </div>
                        <button type="button" onClick={reset} className="w-full py-3 bg-gray-200 text-gray-800 font-bold rounded-lg">Réessayer</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientQrScanner;
